import type {
  DocExample,
  DocField,
  DocParameter,
  DocResource,
  HttpMethod
} from '../types'
import type {
  OpenApiHeader,
  OpenApiHttpMethod,
  OpenApiMediaType,
  OpenApiOperation,
  OpenApiParameter,
  OpenApiResponse,
  OpenApiSpec
} from './openapiSchema.types'
import { describeType, flattenResponseFields, withEnumValues } from './resolveSchema'

const METHODS: OpenApiHttpMethod[] = ['get', 'post', 'put', 'delete', 'patch']

/**
 * A handful of operations keep the slugs of the retired hand-written API
 * Resources pages (e.g. Patient Summary → `get-patient-summary`), so links
 * shared before the docs moved to OpenAPI keep the same endpoint id.
 * Slugifying "Patient Summary" alone would give `patient-summary`.
 */
const SLUG_OVERRIDES: Record<string, string> = {
  'patient-summary': 'get-patient-summary',
  'patient-vitals': 'get-patient-vitals',
  'patient-labs': 'get-patient-labs',
  'patient-diagnoses': 'get-patient-diagnoses',
  'patient-medications': 'get-patient-medications'
}

function slugify(summary: string): string {
  const slug = summary
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
  return SLUG_OVERRIDES[slug] ?? slug
}

/** The operation's parameters in one location, enum values listed in the description. */
function parameters(
  spec: OpenApiSpec,
  operation: OpenApiOperation,
  location: 'path' | 'query' | 'header'
): DocParameter[] | undefined {
  const params = (operation.parameters ?? [])
    .filter(param => param.in === location)
    .map(param => {
      const description = param.description ?? param.schema?.description ?? ''
      return {
        name: param.name,
        type: param.schema ? describeType(spec, param.schema) : 'String',
        required: param.required ?? false,
        description: param.schema ? withEnumValues(spec, param.schema, description) : description
      }
    })
  return params.length > 0 ? params : undefined
}

function responseFields(spec: OpenApiSpec, operation: OpenApiOperation) {
  const schema = operation.responses?.['200']?.content?.['application/json']?.schema
  if (!schema) return undefined
  const fields = flattenResponseFields(spec, schema)
  return fields.length > 0 ? fields : undefined
}

const RESPONSE_REF_PREFIX = '#/components/responses/'
const HEADER_REF_PREFIX = '#/components/headers/'

/** Follows a `$ref` into `components.responses` (the backend's shared error responses). */
function resolveResponse(spec: OpenApiSpec, response: OpenApiResponse): OpenApiResponse {
  if (!response.$ref?.startsWith(RESPONSE_REF_PREFIX)) return response
  return spec.components?.responses?.[response.$ref.slice(RESPONSE_REF_PREFIX.length)] ?? response
}

function resolveHeader(spec: OpenApiSpec, header: OpenApiHeader): OpenApiHeader {
  if (!header.$ref?.startsWith(HEADER_REF_PREFIX)) return header
  return spec.components?.headers?.[header.$ref.slice(HEADER_REF_PREFIX.length)] ?? header
}

/** Headers of the first success or redirect response (rate limit, environment, `Location`). */
function responseHeaders(spec: OpenApiSpec, operation: OpenApiOperation): DocField[] | undefined {
  const status = Object.keys(operation.responses ?? {})
    .filter(code => /^[23]/.test(code))
    .sort()[0]
  if (!status) return undefined
  const headers = resolveResponse(spec, operation.responses![status]).headers ?? {}
  const fields = Object.entries(headers).map(([name, header]) => {
    const resolved = resolveHeader(spec, header)
    return {
      name,
      type: resolved.schema ? describeType(spec, resolved.schema) : 'String',
      description: resolved.description ?? ''
    }
  })
  return fields.length > 0 ? fields : undefined
}

/** The example of a JSON response: `example`, else the first of `examples`, else the schema's. */
function jsonExample(media: OpenApiMediaType | undefined): unknown {
  if (!media) return undefined
  if (media.example !== undefined) return media.example
  const first = Object.values(media.examples ?? {})[0]
  if (first?.value !== undefined) return first.value
  return media.schema?.example ?? media.schema?.examples?.[0]
}

const prettyJson = (value: unknown) => JSON.stringify(value, null, 2)

/** Example body of the first success response, when the backend documents one. */
function responseExample(spec: OpenApiSpec, operation: OpenApiOperation): DocExample | undefined {
  const status = Object.keys(operation.responses ?? {})
    .filter(code => /^2/.test(code))
    .sort()[0]
  if (!status) return undefined
  const example = jsonExample(resolveResponse(spec, operation.responses![status]).content?.['application/json'])
  return example === undefined ? undefined : { status, code: prettyJson(example) }
}

/** Placeholder of a required query parameter: its example, default or first enum value. */
function sampleValue(param: OpenApiParameter): string {
  const schema = param.schema ?? {}
  const value =
    param.example ?? schema.example ?? schema.examples?.[0] ?? schema.default ?? schema.enum?.[0]
  return value === undefined || value === null ? `<${param.name}>` : String(value)
}

/** Shell variables the generated request uses; the reader exports them once. */
export const REQUEST_BASE_URL_VARIABLE = '$SAIVA_API_URL'
export const REQUEST_API_KEY_VARIABLE = '$SAIVA_API_KEY'

/**
 * A `curl` call for the operation, built only from the document: its server
 * (else `$SAIVA_API_URL`), the path with `{param}` placeholders, the required
 * query parameters and the API key header named by the security scheme.
 */
function requestExample(
  spec: OpenApiSpec,
  method: OpenApiHttpMethod,
  path: string,
  operation: OpenApiOperation
): string {
  const base = spec.servers?.[0]?.url?.replace(/\/+$/, '') || REQUEST_BASE_URL_VARIABLE
  const query = (operation.parameters ?? [])
    .filter(param => param.in === 'query' && param.required)
    .map(param => `${param.name}=${sampleValue(param)}`)
  const url = `${base}${path}${query.length ? `?${query.join('&')}` : ''}`

  const lines = [method === 'get' ? `curl "${url}"` : `curl -X ${method.toUpperCase()} "${url}"`]
  for (const scheme of Object.values(spec.components?.securitySchemes ?? {})) {
    if (scheme.type === 'apiKey' && scheme.in === 'header' && scheme.name) {
      lines.push(`-H "${scheme.name}: ${REQUEST_API_KEY_VARIABLE}"`)
    }
  }
  return lines.join(' \\\n  ')
}

/** Slugify-based ids can collide across a large, arbitrary spec; fall back to a unique one. */
function uniqueId(base: string, method: string, path: string, seen: Set<string>): string {
  if (!seen.has(base)) return base
  const fromRoute = slugify(`${method}-${path}`)
  if (!seen.has(fromRoute)) return fromRoute
  let n = 2
  while (seen.has(`${fromRoute}-${n}`)) n++
  return `${fromRoute}-${n}`
}

export interface ResourcesFromOpenApiOptions {
  /** Only include operations carrying this tag. Omit to include every operation. */
  tag?: string
}

/**
 * Converts OpenAPI operations into the `DocResource` shape the doc components
 * render — every operation in the spec, or those carrying `tag`.
 */
export function resourcesFromOpenApi(
  spec: OpenApiSpec,
  { tag }: ResourcesFromOpenApiOptions = {}
): DocResource[] {
  const resources: DocResource[] = []
  const seenIds = new Set<string>()

  for (const [path, pathItem] of Object.entries(spec.paths)) {
    for (const method of METHODS) {
      const operation = pathItem[method]
      if (!operation) continue
      if (tag && !operation.tags?.includes(tag)) continue
      const requestTitle = operation.summary ?? path
      const id = uniqueId(slugify(requestTitle), method, path, seenIds)
      seenIds.add(id)

      resources.push({
        id,
        method: method.toUpperCase() as HttpMethod,
        path,
        requestTitle,
        description: operation.description ?? requestTitle,
        pathParameters: parameters(spec, operation, 'path'),
        queryParameters: parameters(spec, operation, 'query'),
        headerParameters: parameters(spec, operation, 'header'),
        responseFields: responseFields(spec, operation),
        responseHeaders: responseHeaders(spec, operation),
        requestExample: requestExample(spec, method, path, operation),
        responseExample: responseExample(spec, operation),
        tags: operation.tags
      })
    }
  }

  return resources
}
